/**
 * PendingConfirmCard (SK-CONFIRM, T-09, DS-1g-03 §3.1) — AC-1g-11 (client
 * leg). Renders on a DELIVERED capture carrying a pending `await_confirm`
 * (daily log + a home-screen banner, per caller placement — this component
 * has no opinion on where it is mounted, matching the rest of §5.1's
 * inventory). Candidate summary + one-tap **Confirm** / one-tap
 * **Correct** (which opens a local, inline correction-note affordance —
 * §5's correction affordance, kept self-contained for Stage A rather than
 * re-navigating into SaveScreen). Both verdicts ride
 * `submitConfirmVerdict()` (T-09's `pending-confirm.ts`), idempotently, on
 * the same outbox/dispatcher machinery as any other capture.
 *
 * Timeout/expiry (design §3.1): once `pendingConfirmStatus()` reads
 * `"expired"`, the tap affordances are REPLACED by a re-open-from-daily-log
 * affordance — a late Turn B is never blindly retried from here.
 *
 * The labeled `Confirmation`/`Correction` EMISSION is server-side (T-28);
 * this component only ever drives the client-side verdict submission
 * (AC-1g-11's live proof is GATED on T-28 + 1l serving, S8).
 */

import { useState } from "preact/hooks";
import { pendingConfirmStatus, submitConfirmVerdict, type ConfirmVerdict } from "../confirm/pending-confirm";
import type { OutboxRecord } from "../outbox/types";

export interface PendingConfirmCardProps {
  record: OutboxRecord;
  now?: Date;
  onResolved?: (original_capture_id: string, verdict_capture_id: string, verdict: ConfirmVerdict) => void;
  onReopen?: (capture_id: string) => void;
}

export function PendingConfirmCard({ record, now, onResolved, onReopen }: PendingConfirmCardProps) {
  const [correcting, setCorrecting] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [locallyResolved, setLocallyResolved] = useState(false);

  const status = pendingConfirmStatus(record, now);
  const field = record.envelope.payload["confirm_pending"] as
    | { confirm_token: string; deadline: string; candidate_summary: string }
    | undefined;

  if (status === "none" || !field || locallyResolved) return null;

  async function submit(verdict: ConfirmVerdict, correctionNote?: string) {
    try {
      const verdictRecord = await submitConfirmVerdict(record, verdict, correctionNote);
      // Hide only after both the verdict release and disposition tombstone are
      // durable. A failed release therefore never creates a false resolved
      // interval in the active session or after reload.
      setLocallyResolved(true);
      onResolved?.(record.capture_id, verdictRecord.capture_id, verdict);
    } catch {
      // Persistence failed: retain the truthful unresolved card.
    }
  }

  async function handleConfirm() {
    await submit("confirm");
  }

  function openCorrect() {
    setCorrecting(true);
    setNoteDraft("");
  }

  async function handleCorrectSubmit() {
    const trimmed = noteDraft.trim();
    if (!trimmed) return; // human-typed correction required, no default text
    await submit("correct", trimmed);
    setCorrecting(false);
    setNoteDraft("");
  }

  return (
    <div data-skin-slot="SK-CONFIRM" data-testid={`pending-confirm-card-${record.capture_id}`}>
      <p data-testid={`pending-confirm-summary-${record.capture_id}`}>{field.candidate_summary}</p>
      <p data-testid={`pending-confirm-deadline-${record.capture_id}`}>{field.deadline}</p>

      {status === "pending" && (
        <>
          <button
            type="button"
            data-testid={`pending-confirm-confirm-${record.capture_id}`}
            onClick={handleConfirm}
          >
            Confirm
          </button>
          <button
            type="button"
            data-testid={`pending-confirm-correct-${record.capture_id}`}
            onClick={openCorrect}
          >
            Correct
          </button>
          <button
            type="button"
            data-testid={`pending-confirm-delete-${record.capture_id}`}
            onClick={() => void submit("delete")}
          >
            Delete
          </button>
          {correcting && (
            <div data-testid={`pending-confirm-correct-form-${record.capture_id}`}>
              <textarea
                data-testid={`pending-confirm-correct-note-${record.capture_id}`}
                value={noteDraft}
                onInput={(e) => setNoteDraft((e.target as HTMLTextAreaElement).value)}
              />
              <button
                type="button"
                data-testid={`pending-confirm-correct-submit-${record.capture_id}`}
                disabled={noteDraft.trim().length === 0}
                onClick={handleCorrectSubmit}
              >
                Submit correction
              </button>
            </div>
          )}
        </>
      )}

      {status === "expired" && (
        <div data-testid={`pending-confirm-expired-${record.capture_id}`}>
          <p>This ask expired.</p>
          <button
            type="button"
            data-testid={`pending-confirm-reopen-${record.capture_id}`}
            onClick={() => onReopen?.(record.capture_id)}
          >
            Re-open from daily log
          </button>
        </div>
      )}
    </div>
  );
}
